/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import 'typeface-inter';
import FontFaceObserver from 'fontfaceobserver';
import { IntlProvider, useIntl } from 'react-intl';
import { locale, messages } from '../../intl';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../OCLCMaterialTheme';
import { setIntlObject } from '../../utils/intlUtils';

const AppWrapper = ({ children }) => {
  const intl = useIntl();
  setIntlObject(intl);
  return <>{children}</>;
};

/**
 * Wrapper for the application entry point.
 *
 * @param props
 * @return {*}
 * @constructor
 */
const App = ({ children }) => {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    const interFont = new FontFaceObserver('Inter');

    Promise.all([interFont.load()]).then(
      () => {
        setFontsLoaded(true);
      },
      () => {
        // At this point, consider the fonts loaded and render the view with what we got.
        setFontsLoaded(true);
      },
    );
  }, []);

  return fontsLoaded ? (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        {/* CssBaseline MUST be inside ThemeProvider in order to create baseline off of custom theme! */}
        <CssBaseline />
        <IntlProvider locale={locale} messages={messages}>
          <AppWrapper>{children}</AppWrapper>
        </IntlProvider>
      </ThemeProvider>
    </HelmetProvider>
  ) : null;
};

export default App;
